import { IS_LOCAL } from '@/helpers';

const I18N = {
  ko: {
    dailyMissionDetail: {
      dateFormat: 'yy.LL.dd',
    },
  },
  en: {
    dailyMissionDetail: {
      dateFormat: 'LLL dd, yyyy',
    },
  },
};

const GrowingStore = {
  namespaced: true,
  state() {
    return {
      // 데일리 미션 상태
      dailyMissionStatus: {
        attendance: 1,
        invite: 0,
        subs: 0,
        rentalIncome: 0,
        swap: 0,
      },
      // 클로버 단계
      step: 0,
      // 데일리 미션 상세
      dailyMissionDetail: [],
      // 사용 전 물뿌리개
      unusedWateringCan: 0,
      // 총 누적 물뿌리개
      totalWateringCan: 0,
      // 나의 완성 클로버 상태
      myCompletedCloverStatus: {
        usrCompleteClovers: 0,
        receivedCloverRewards: [],
      },
    };
  },
  getters: {
    getDailyMissionDetailSum: (state) => state.dailyMissionDetail.reduce((acc, item) => acc + item.value, 0),
  },
  mutations: {
    setDailyMissionStatus(state, status) {
      state.dailyMissionStatus = { ...status };
      state.dailyMissionStatus.attendance = 1;
    },
    setGameStep(state, step) {
      state.step = step;
    },
    setGameStatus(state, status) {
      state.step = status.step;
      state.unusedWateringCan = status.unused;
      state.totalWateringCan = status.total;
    },
    setDailyMissionDetail(state, detail) {
      state.dailyMissionDetail = detail;
    },
    setMyCompletedCloverStatus(state, status) {
      state.myCompletedCloverStatus = status;
    },
  },
  actions: {
    async fetch(_, { method, url, params }) {
      const data = method === 'get' ? { params: { ...params } } : params;
      const response = await this._vm.$axios[method](`${process.env.VUE_APP_TEST_SERVER}${url}`, data);
      return response;
    },
    /**
     * 데일리 미션 상태 조회
     * @returns {Promise<{
     *   invite: number;
     *   subs: number;
     *   rentalIncome: number;
     *   swap: number;
     * }>}
     */
    async getDailyMissionStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            invite: 4,
            subs: 2,
            rentalIncome: 1,
            swap: 1,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/growing/mission',
      });
      return response;
    },
    /**
     * 데일리 미션 상태 조회
     */
    async fetchDailyMissionStatus({ dispatch, commit }) {
      const { data: dailyMissionStatus } = await dispatch('getDailyMissionStatus');

      commit('setDailyMissionStatus', dailyMissionStatus);

      return;
    },
    /**
     * 데일리 미션 상세 조회
     * @returns {Promise<{
     *   date: string;
     *   content: string;
     *   value: number;
     * }[]>}
     */
    async getDailyMissionDetail({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: [
            {
              date: '2023-01-01T00:00:00',
              content: '출석 체크 완료',
              value: 1,
            },
            {
              date: '2023-01-02T00:00:00',
              content: '출석 체크 완료',
              value: 4,
            },
          ],
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/growing/mission/detail',
      });
      return response;
    },
    /**
     * 데일리 미션 상세 조회
     */
    async fetchDailyMissionDetail({ dispatch, commit, rootState }) {
      const { data: dailyMissionDetail } = await dispatch('getDailyMissionDetail');
      const { lang } = rootState;

      const format = I18N[lang].dailyMissionDetail.dateFormat;
      const formattedDailyMissionDetail = dailyMissionDetail.map((item) => ({
        ...item,
        date: this._vm.$tl(item.date, lang).toFormat(format),
      }));

      commit('setDailyMissionDetail', formattedDailyMissionDetail);
      return;
    },
    /**
     * @description 게임 상태 조회
     */
    async getGameStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            step: 2,
            unused: 5,
            total: 9,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/growing/status',
      });
      return response;
    },
    /** 게임 상태 조회 */
    async fetchGameStatus({ dispatch, commit }) {
      const { data: gameStatus } = await dispatch('getGameStatus');

      commit('setGameStatus', gameStatus);

      return;
    },
    /**
     * @description 나의 완성 클로버 상태 조회
     */
    async getMyCompletedCloverStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            usrCompleteClovers: 5,
            receivedCloverRewards: [1],
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/growing/reward/complete',
      });
      return response;
    },
    /** 나의 완성 클로버 상태 조회 */
    async fetchMyCompletedCloverStatus({ dispatch, commit }) {
      const { data: myCompletedCloverStatus } = await dispatch('getMyCompletedCloverStatus');

      commit('setMyCompletedCloverStatus', myCompletedCloverStatus);

      return;
    },
    /**
     * @description 클로버 완성 보상 받기
     * @returns {Promise<{ omc: number; clover: number }>}
     */
    async postCloverReward({ dispatch }) {
      if (IS_LOCAL) {
        // return {
        //   data: null,
        // };
        return {
          data: {
            omc: 1,
            clover: 4,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/growing/watering',
      });
      return response;
    },
    /** 클로버 완성 보상 받기 */
    async submitCloverReward({ dispatch }) {
      try {
        const { data } = await dispatch('postCloverReward');
        return data === '' ? true : data;
      } catch (error) {
        if (!error.response?.data?.message) return false;

        return error.response.data.message;
      }
    },
    /** 완성 클로버 보상 받기 */
    async postCompletedCloverReward({ dispatch }, index) {
      if (IS_LOCAL) {
        return {
          data: {
            omc: 1,
            lCoupon: 2,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/growing/reward/complete',
        params: {
          index: index,
        },
      });
      return response;
    },
    /** 완성 클로버 보상 받기 */
    async submitCompletedCloverReward({ dispatch }, index) {
      try {
        const { data } = await dispatch('postCompletedCloverReward', index);

        return data;
      } catch (error) {
        if (!error.response?.data?.message) return false;

        return error.response.data.message;
      } finally {
        dispatch('fetchMyCompletedCloverStatus');
      }
    },
  },
};

export default GrowingStore;
